import { ChevronDownIcon } from "@chakra-ui/icons";
import { Button } from "@chakra-ui/react";
import React, { useState } from "react";
import { StandardSelectOption } from "./parts/SelectOption";
import { SingleSelectButtonContent } from "./Selector";
import { SingleSelector, SingleSelectProps } from "./SingleSelector";

export type LazySingleSelectorProps<T extends string> = SingleSelectProps<T> & {
  selectedOption: StandardSelectOption<T> | undefined;
};

/**
 * Renders a static placeholder button initially and only loads the expensive
 * Selector on the first interaction.
 */
export function LazySingleSelector<T extends string>({
  selectedOption,
  size,
  isLoading,
  placeholder,
  onOpen,
  isRequired,
  isInvalid,
  isDisabled,
  expandIcon = <ChevronDownIcon />,
  buttonProps,
  ...props
}: LazySingleSelectorProps<T>): React.ReactElement {
  const [activated, setActivated] = useState(false);
  const [shouldOpenOnMount, setShouldOpenOnMount] = useState(false);

  const preload = () => {
    setShouldOpenOnMount(false);
    setActivated(true);
  };

  const activateAndOpen = () => {
    setShouldOpenOnMount(true);
    setActivated(true);
  };

  const hasSelection = selectedOption !== undefined;

  if (!activated) {
    return (
      <Button
        {...buttonProps}
        variant="outline"
        size={size}
        onPointerEnter={preload}
        onClick={activateAndOpen}
        onKeyDown={(e) => {
          if (isDisabled) {
            return;
          }

          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();

            activateAndOpen();
          }
        }}
        borderColor={!isInvalid && hasSelection ? "gray.500" : undefined}
        fontWeight={hasSelection ? "medium" : "normal"}
        color={hasSelection ? "gray.700" : "gray.500"}
        aria-required={isRequired}
        aria-invalid={isInvalid}
        isDisabled={isDisabled}
        justifyContent="space-between"
        rightIcon={expandIcon}
      >
        <SingleSelectButtonContent
          selectedOption={selectedOption}
          isLoading={isLoading}
          placeholder={placeholder}
        />
      </Button>
    );
  }

  return (
    <SingleSelector
      {...props}
      size={size}
      isLoading={isLoading}
      placeholder={placeholder}
      initialOpenState={shouldOpenOnMount}
      isRequired={isRequired}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      expandIcon={expandIcon}
      buttonProps={buttonProps}
      onOpen={onOpen}
    />
  );
}
